import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CSSProperties } from "@mui/styles";
import { translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable } from "constants/constants";
import { IFailureCode } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { IDefaultFailureType } from "entities/FailureDetailsEntity/FailureDetailsEntityInterfaces";
import { EventReportsEntity } from "entities/EventReports/EventReportsEntity";
import { IOption } from "../Dropdown";
import { SegmentFieldVariant, SegmentSelect } from "./SegmentSelect";

interface IFailureCodeFieldProps {
    machineCenterId: number;
    failureTypeId?: number;
    functionalLocationId?: number;
    locationSpecific?: boolean;
    defaultFailureTypes: IDefaultFailureType[];
    value?: number;
    obligatory?: boolean;
    inlineStyle?: CSSProperties;
    variant?: SegmentFieldVariant;
    placeholder?: string;
    onChange: (failureCodeId: number) => void;
}

/**
 * Controlled failure-code selector reused by the inline table edit and the split
 * modal. Builds the same option set as the original FailureCodeDetailsDropdown:
 * location-specific + non-location-specific + default failure codes, filtered to the
 * selected failure type.
 */
export const FailureCodeField = ({
    machineCenterId,
    failureTypeId,
    functionalLocationId,
    locationSpecific,
    defaultFailureTypes,
    value,
    obligatory = true,
    inlineStyle,
    variant = "inline",
    placeholder,
    onChange,
}: IFailureCodeFieldProps) => {
    const { i18n } = useTranslation();

    const { loading, locationSpecificFailureCodes, nonLocationSpecificFailureCodes } =
        EventReportsEntity.GetLocationSpecificFailureCodesForSecondaryCodesEdition(machineCenterId, functionalLocationId);

    const nonLocationSpecificOptions = useMemo(
        () =>
            nonLocationSpecificFailureCodes.map((failureCode) => ({
                codeId: failureCode.failure_code.failure_code_id,
                name: translateDefaultEntity(
                    i18n.language,
                    failureCode.failure_code as unknown as IFailureCode,
                    "name",
                    DefaultTranslationFieldsInTable.FAILURE_CODE,
                ),
                typeId: failureCode.failure_code.failure_type_id,
            })) || [],
        [nonLocationSpecificFailureCodes, i18n],
    );

    const locationSpecificOptions = useMemo(
        () =>
            locationSpecificFailureCodes.map((failureCode) => ({
                codeId: failureCode.failure_code.failure_code_id,
                name: translateDefaultEntity(
                    i18n.language,
                    failureCode.failure_code as unknown as IFailureCode,
                    "name",
                    DefaultTranslationFieldsInTable.FAILURE_CODE,
                ),
                typeId: failureCode.failure_code.failure_type_id,
            })),
        [locationSpecificFailureCodes, i18n],
    );

    const defaultOptions = useMemo(
        () =>
            defaultFailureTypes.reduce(
                (accumulator: { codeId: number; name: string; typeId: number }[], failureType) => [
                    ...accumulator,
                    ...failureType.failure_codes.map((failureCode) => ({
                        codeId: failureCode.failure_code_id,
                        name: translateDefaultEntity(
                            i18n.language,
                            failureCode as unknown as IFailureCode,
                            "name",
                            DefaultTranslationFieldsInTable.FAILURE_CODE,
                        ),
                        typeId: failureType.failure_type_id,
                    })),
                ],
                [],
            ),
        [defaultFailureTypes, i18n],
    );

    const allFailureCodeOptions = useMemo(
        () => [...nonLocationSpecificOptions, ...locationSpecificOptions, ...defaultOptions],
        [locationSpecificOptions, defaultOptions, nonLocationSpecificOptions],
    );

    const failureCodesOptionsBasedOnSelection: IOption[] = useMemo(
        () =>
            allFailureCodeOptions.reduce((accumulator: IOption[], failureCodeOption) => {
                let failureCodeOptionsAcc = accumulator;
                if (failureTypeId === failureCodeOption.typeId) {
                    if (!failureCodeOptionsAcc.find((failureCodeAcc) => failureCodeOption.codeId === failureCodeAcc.id))
                        failureCodeOptionsAcc = [
                            ...failureCodeOptionsAcc,
                            { id: failureCodeOption.codeId, name: failureCodeOption.name },
                        ];
                }
                return failureCodeOptionsAcc;
            }, []),
        [allFailureCodeOptions, failureTypeId],
    );

    const currentSelectionInOptions = useMemo(
        () => failureCodesOptionsBasedOnSelection.some((option) => option.id === value),
        [value, failureCodesOptionsBasedOnSelection],
    );

    return (
        <SegmentSelect
            variant={variant}
            value={value}
            options={failureCodesOptionsBasedOnSelection}
            onChange={onChange}
            loading={loading}
            obligatory={obligatory}
            disabled={!failureTypeId || (locationSpecific && !functionalLocationId)}
            pending={!Boolean(value) || !currentSelectionInOptions}
            placeholder={placeholder}
            inlineStyle={inlineStyle}
        />
    );
};
